import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, fileUrl, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, X, Receipt, CalendarClock, Search, Wallet,
  TrendingUp, AlertTriangle, Share2, FileText,
} from "lucide-react";
import { Pagination, SortableHeader, ColumnVisibility, compareValues } from "../components/DataTable";
import ConfirmDialog from "../components/ConfirmDialog";
import useLocalStorageState from "../hooks/useLocalStorageState";

const PLAN_OPTIONS = ["monthly", "quarterly", "yearly", "custom"];
const METHOD_OPTIONS = ["upi", "bank", "cash", "card", "other"];
const STATUS_OPTIONS = ["paid", "refunded", "void"];

const PLAN_TO_DAYS = { monthly: 30, quarterly: 91, yearly: 365 };

const COLUMNS = [
  { key: "invoice_number", label: "Invoice", required: true },
  { key: "restaurant_name", label: "Restaurant", required: true },
  { key: "amount", label: "Amount" },
  { key: "plan", label: "Plan" },
  { key: "period", label: "Period" },
  { key: "payment_date", label: "Paid on" },
  { key: "method", label: "Method" },
  { key: "reference", label: "Reference" },
  { key: "status", label: "Status" },
  { key: "is_current", label: "Live now" },
];

const emptyForm = {
  restaurant_id: "",
  amount: "",
  currency: "INR",
  plan: "monthly",
  period_start: "",
  period_end: "",
  payment_date: new Date().toISOString().slice(0, 10),
  method: "upi",
  reference: "",
  notes: "",
  receipt_file_id: "",
  status: "paid",
};

const inputCls = "w-full bg-white border border-black/10 rounded-sm px-3 py-2 focus:ring-2 focus:ring-[var(--brand-primary)]/20 focus:border-[var(--brand-primary)] outline-none";

function fmtMoney(amount, currency = "INR") {
  const n = Number(amount) || 0;
  try {
    return n.toLocaleString("en-IN", { style: "currency", currency, maximumFractionDigits: 0 });
  } catch { return `${currency} ${n}`; }
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}

function addDays(iso, days) {
  if (!iso) return "";
  const d = new Date(iso); d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

const STATUS_PILL = {
  paid:     "bg-emerald-50 text-emerald-700 border-emerald-200",
  refunded: "bg-amber-50 text-amber-800 border-amber-200",
  void:     "bg-zinc-100 text-zinc-600 border-zinc-200",
};

export default function AdminPayments() {
  const [payments, setPayments] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // ad object | "new" | null
  const [removing, setRemoving] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState("");
  const [planFilter, setPlanFilter] = useState("all");

  const [sort, setSort] = useLocalStorageState("payments.sort", { key: "payment_date", dir: "desc" });
  const [pageSize, setPageSize] = useLocalStorageState("payments.pageSize", 25);
  const [page, setPage] = useState(1);
  const [hiddenCols, setHiddenCols] = useLocalStorageState(
    "admin_payments_hidden_cols_v1",
    new Set(["reference"]),
  );
  const toggleCol = (k) => setHiddenCols((prev) => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const showCol = useCallback((k) => !hiddenCols.has(k), [hiddenCols]);
  const onSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, rRes, sRes] = await Promise.all([
        api.get("/admin/payments"),
        api.get("/restaurants"),
        api.get("/admin/payments/summary"),
      ]);
      setPayments(pRes.data || []);
      setRestaurants((rRes.data || []).filter((r) => r.is_active));
      setSummary(sRes.data || null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load payments");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Auto-compute period_end from period_start + plan
  useEffect(() => {
    if (!form.period_start) return;
    const days = PLAN_TO_DAYS[form.plan];
    if (!days) return;
    const computed = addDays(form.period_start, days - 1);
    if (form.period_end !== computed) {
      setForm((f) => ({ ...f, period_end: computed }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.period_start, form.plan]);

  const openNew = () => {
    setForm({ ...emptyForm, payment_date: new Date().toISOString().slice(0, 10) });
    setEditing("new");
  };
  const openEdit = (p) => {
    setForm({
      restaurant_id: p.restaurant_id || "",
      amount: String(p.amount ?? ""),
      currency: p.currency || "INR",
      plan: p.plan || "monthly",
      period_start: p.period_start || "",
      period_end: p.period_end || "",
      payment_date: p.payment_date || "",
      method: p.method || "upi",
      reference: p.reference || "",
      notes: p.notes || "",
      receipt_file_id: p.receipt_file_id || "",
      status: p.status || "paid",
    });
    setEditing(p);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.restaurant_id) { toast.error("Pick a restaurant"); return; }
    if (!form.amount || Number(form.amount) < 0) { toast.error("Enter a valid amount"); return; }
    if (!form.period_start || !form.period_end) { toast.error("Period start & end are required"); return; }
    if (form.period_end < form.period_start) { toast.error("Period end must be after start"); return; }
    setSaving(true);
    try {
      const payload = {
        restaurant_id: form.restaurant_id,
        amount: Number(form.amount),
        currency: form.currency || "INR",
        plan: form.plan,
        period_start: form.period_start,
        period_end: form.period_end,
        payment_date: form.payment_date,
        method: form.method,
        reference: form.reference || "",
        notes: form.notes || "",
        receipt_file_id: form.receipt_file_id || null,
        status: form.status,
      };
      if (editing === "new") {
        await api.post("/admin/payments", payload);
        toast.success("Payment recorded");
      } else {
        await api.put(`/admin/payments/${editing.id}`, payload);
        toast.success("Payment updated");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!removing) return;
    try {
      await api.delete(`/admin/payments/${removing.id}`);
      toast.success("Payment deleted");
      setRemoving(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const uploadReceipt = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, receipt_file_id: r.data.id }));
      toast.success("Receipt uploaded");
    } catch { toast.error("Upload failed"); }
  };

  const sendWhatsAppReceipt = (p) => {
    const rest = restaurants.find((r) => r.id === p.restaurant_id);
    const phone = (rest?.phone || "").replace(/[^0-9]/g, "");
    const text = encodeURIComponent(
      `Hi ${p.restaurant_name},\n\n` +
      `Receipt for your RestroSetup ad payment:\n` +
      `• Invoice: ${p.invoice_number}\n` +
      `• Amount: ${fmtMoney(p.amount, p.currency)}\n` +
      `• Plan: ${p.plan}\n` +
      `• Period: ${fmtDate(p.period_start)} → ${fmtDate(p.period_end)}\n` +
      `• Paid via: ${p.method}${p.reference ? ` (Ref: ${p.reference})` : ""}\n\n` +
      `Thanks for your business! — Team RestroSetup`
    );
    const url = phone
      ? `https://wa.me/${phone}?text=${text}`
      : `https://wa.me/?text=${text}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // Filter + sort + paginate
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return payments.filter((p) => {
      if (planFilter !== "all" && p.plan !== planFilter) return false;
      if (!q) return true;
      const hay = `${p.invoice_number} ${p.restaurant_name} ${p.reference || ""} ${p.notes || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [payments, search, planFilter]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const av = a[sort.key]; const bv = b[sort.key];
      return compareValues(av, bv) * (sort.dir === "asc" ? 1 : -1);
    });
    return arr;
  }, [filtered, sort]);

  const total = sorted.length;
  const paged = useMemo(() => sorted.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize),
    [sorted, page, pageSize]);
  useEffect(() => { setPage(1); }, [pageSize, search, planFilter]);

  return (
    <div data-testid="admin-payments-page" className="p-6 lg:p-10 space-y-8 w-full">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#525252] mb-1">Manage</div>
          <h1 className="font-display text-3xl font-black inline-flex items-center gap-2">
            <Wallet size={26}/> Payments
          </h1>
          <p className="text-sm text-[#525252] mt-1 max-w-xl">
            Record ad fees collected from restaurants. Ads automatically pause when the paid period ends.
          </p>
        </div>
        <button onClick={openNew} data-testid="add-payment-button"
                className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white px-4 py-2 rounded-sm inline-flex items-center gap-2 text-sm font-medium shrink-0">
          <Plus size={16}/> Record payment
        </button>
      </div>

      {/* Summary stat tiles */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="payments-summary">
          <StatTile label="This month" value={fmtMoney(summary.total_this_month)} icon={<TrendingUp size={16}/>}/>
          <StatTile label="This year"  value={fmtMoney(summary.total_this_year)}  icon={<TrendingUp size={16}/>}/>
          <StatTile label="All-time"   value={fmtMoney(summary.total_all_time)}   icon={<Wallet size={16}/>}/>
          <StatTile label="Total invoices" value={summary.paid_count}             icon={<Receipt size={16}/>}/>
        </div>
      )}

      {/* "Expired" callout — only kept; the "expiring soon" callout was removed
          since it was just visual noise that auto-hid itself anyway. */}
      {summary && summary.expired.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <CalloutList
            tone="danger"
            icon={<AlertTriangle size={14}/>}
            title="Expired — ads now hidden"
            items={summary.expired.map((e) => ({
              label: e.restaurant_name, hint: `Ended ${fmtDate(e.period_end)}`,
            }))}
          />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 border border-black/10 rounded-sm bg-white px-3 py-2">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]"/>
          <input data-testid="payments-search" value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Search invoice / restaurant / reference"
                 className="pl-7 pr-3 py-1.5 text-sm border border-black/10 rounded-sm w-80 outline-none focus:border-[var(--brand-primary)]"/>
        </div>
        <select data-testid="payments-plan-filter" value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}
                className="text-sm border border-black/10 rounded-sm px-2 py-1.5">
          <option value="all">All plans</option>
          {PLAN_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <div className="flex-1"/>
        <ColumnVisibility columns={COLUMNS} hidden={hiddenCols} onToggle={toggleCol} testId="payments-cols"/>
      </div>

      {loading ? (
        <div className="text-sm text-[#525252]">Loading…</div>
      ) : payments.length === 0 ? (
        <div className="border border-dashed border-black/10 rounded-sm bg-white p-8 text-sm text-[#525252] text-center">
          No payments recorded yet. Click <strong>Record payment</strong> to log the first one.
        </div>
      ) : (
        <>
          <div className="overflow-x-auto border border-black/10 rounded-sm bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-black/10 bg-[#FAFAFA]">
                  {showCol("invoice_number") && (
                    <th className="px-4 py-3 text-left">
                      <SortableHeader label="Invoice" sortKey="invoice_number" currentSort={sort.key} currentDir={sort.dir} onSort={onSort}/>
                    </th>
                  )}
                  {showCol("restaurant_name") && (
                    <th className="px-4 py-3 text-left">
                      <SortableHeader label="Restaurant" sortKey="restaurant_name" currentSort={sort.key} currentDir={sort.dir} onSort={onSort}/>
                    </th>
                  )}
                  {showCol("amount") && (
                    <th className="px-4 py-3 text-left">
                      <SortableHeader label="Amount" sortKey="amount" currentSort={sort.key} currentDir={sort.dir} onSort={onSort}/>
                    </th>
                  )}
                  {showCol("plan") && <th className="px-4 py-3 text-left text-xs uppercase tracking-[0.15em] text-[#525252]">Plan</th>}
                  {showCol("period") && <th className="px-4 py-3 text-left text-xs uppercase tracking-[0.15em] text-[#525252]">Period</th>}
                  {showCol("payment_date") && (
                    <th className="px-4 py-3 text-left">
                      <SortableHeader label="Paid on" sortKey="payment_date" currentSort={sort.key} currentDir={sort.dir} onSort={onSort}/>
                    </th>
                  )}
                  {showCol("method") && <th className="px-4 py-3 text-left text-xs uppercase tracking-[0.15em] text-[#525252]">Method</th>}
                  {showCol("reference") && <th className="px-4 py-3 text-left text-xs uppercase tracking-[0.15em] text-[#525252]">Ref</th>}
                  {showCol("status") && <th className="px-4 py-3 text-left text-xs uppercase tracking-[0.15em] text-[#525252]">Status</th>}
                  {showCol("is_current") && <th className="px-4 py-3 text-left text-xs uppercase tracking-[0.15em] text-[#525252]">Live now</th>}
                  <th className="px-4 py-3 text-right text-xs uppercase tracking-[0.15em] text-[#525252]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((p) => (
                  <tr key={p.id} data-testid={`payment-row-${p.id}`} className="border-b border-black/5 hover:bg-[#FAFAFA]">
                    {showCol("invoice_number") && <td className="px-4 py-3 font-mono text-xs">{p.invoice_number}</td>}
                    {showCol("restaurant_name") && <td className="px-4 py-3 font-medium">{p.restaurant_name}</td>}
                    {showCol("amount") && <td className="px-4 py-3 font-semibold">{fmtMoney(p.amount, p.currency)}</td>}
                    {showCol("plan") && <td className="px-4 py-3 capitalize text-xs">{p.plan}</td>}
                    {showCol("period") && (
                      <td className="px-4 py-3 text-xs whitespace-nowrap">{fmtDate(p.period_start)} → {fmtDate(p.period_end)}</td>
                    )}
                    {showCol("payment_date") && <td className="px-4 py-3 text-xs whitespace-nowrap">{fmtDate(p.payment_date)}</td>}
                    {showCol("method") && <td className="px-4 py-3 uppercase text-[10px] tracking-wider text-[#525252]">{p.method}</td>}
                    {showCol("reference") && <td className="px-4 py-3 text-xs text-[#525252] truncate max-w-[140px]" title={p.reference}>{p.reference || "—"}</td>}
                    {showCol("status") && (
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-wider border rounded-sm ${STATUS_PILL[p.status] || ""}`}>
                          {p.status}
                        </span>
                      </td>
                    )}
                    {showCol("is_current") && (
                      <td className="px-4 py-3">
                        {p.is_current
                          ? <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" title="Currently active"/>
                          : <span className="inline-block w-2 h-2 rounded-full bg-zinc-300" title="Not covering today"/>}
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <div className="flex gap-1 justify-end">
                        {p.receipt_file_id && (
                          <a href={fileUrl(p.receipt_file_id)} target="_blank" rel="noreferrer"
                             data-testid={`receipt-${p.id}`}
                             title="Open receipt" className="p-2 hover:bg-black/5 rounded-sm">
                            <FileText size={16}/>
                          </a>
                        )}
                        <button onClick={() => sendWhatsAppReceipt(p)} data-testid={`whatsapp-${p.id}`}
                                title="Send receipt via WhatsApp"
                                className="p-2 hover:bg-emerald-50 text-emerald-700 rounded-sm"><Share2 size={16}/></button>
                        <button onClick={() => openEdit(p)} data-testid={`edit-payment-${p.id}`}
                                title="Edit" className="p-2 hover:bg-black/5 rounded-sm"><Pencil size={16}/></button>
                        <button onClick={() => setRemoving(p)} data-testid={`delete-payment-${p.id}`}
                                title="Delete" className="p-2 hover:bg-[var(--brand-primary)]/5 text-[var(--brand-primary)] rounded-sm">
                          <Trash2 size={16}/>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} pageSize={pageSize} total={total}
                      onPageChange={setPage} onPageSizeChange={setPageSize}/>
        </>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex" data-testid="payment-modal">
          <div className="flex-1 bg-black/40" onClick={() => setEditing(null)} />
          <form onSubmit={submit} className="w-full max-w-lg bg-white h-full overflow-y-auto p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl font-bold">
                {editing === "new" ? "Record Payment" : `Edit ${editing.invoice_number}`}
              </h2>
              <button type="button" onClick={() => setEditing(null)} className="p-2 hover:bg-black/5"><X size={18}/></button>
            </div>

            <div className="space-y-5">
              <Field label="Restaurant *">
                <select data-testid="payment-restaurant" required value={form.restaurant_id}
                        onChange={(e) => setForm({ ...form, restaurant_id: e.target.value })}
                        className={inputCls}>
                  <option value="">— Select —</option>
                  {restaurants.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Amount *">
                  <div className="relative">
                    <span data-testid="payment-currency-prefix"
                          style={{ fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif" }}
                          className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0A0A0A] font-semibold pointer-events-none select-none">
                      {form.currency === "INR" ? "₹" : form.currency}
                    </span>
                    <input data-testid="payment-amount"
                           required
                           type="number"
                           inputMode="decimal"
                           min="0"
                           step="0.01"
                           placeholder="0.00"
                           value={form.amount}
                           onChange={(e) => setForm({ ...form, amount: e.target.value })}
                           onWheel={(e) => e.target.blur()}
                           className={`${inputCls} pl-8 text-right tabular-nums no-spinner`}/>
                  </div>
                  <input type="hidden" data-testid="payment-currency" value={form.currency} readOnly/>
                </Field>
                <Field label="Plan">
                  <select data-testid="payment-plan" value={form.plan}
                          onChange={(e) => setForm({ ...form, plan: e.target.value })}
                          className={inputCls}>
                    {PLAN_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </Field>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Period from *">
                  <input data-testid="payment-period-start" required type="date" value={form.period_start}
                         onChange={(e) => setForm({ ...form, period_start: e.target.value })}
                         className={inputCls}/>
                </Field>
                <Field label="Period to *">
                  <input data-testid="payment-period-end" required type="date" value={form.period_end}
                         onChange={(e) => setForm({ ...form, period_end: e.target.value })}
                         className={inputCls}/>
                </Field>
              </div>
              <p className="text-[10px] text-[#525252] -mt-3">
                End date auto-fills from the selected plan. Edit freely for custom periods.
                Ads for this restaurant remain live during this window.
              </p>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Payment date">
                  <input data-testid="payment-date" type="date" value={form.payment_date}
                         onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
                         className={inputCls}/>
                </Field>
                <Field label="Method">
                  <select data-testid="payment-method" value={form.method}
                          onChange={(e) => setForm({ ...form, method: e.target.value })}
                          className={inputCls}>
                    {METHOD_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </Field>
              </div>

              <Field label="Reference / transaction ID">
                <input data-testid="payment-reference" type="text" value={form.reference} maxLength={200}
                       placeholder="UPI-TXN-…, NEFT ref, cheque no…"
                       onChange={(e) => setForm({ ...form, reference: e.target.value })}
                       className={inputCls}/>
              </Field>

              <Field label="Notes (internal)">
                <textarea data-testid="payment-notes" value={form.notes} maxLength={1000} rows={2}
                          onChange={(e) => setForm({ ...form, notes: e.target.value })}
                          className={inputCls}/>
              </Field>

              <Field label="Status">
                <select data-testid="payment-status" value={form.status}
                        onChange={(e) => setForm({ ...form, status: e.target.value })}
                        className={inputCls}>
                  {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>

              <Field label="Receipt PDF / image (optional)">
                <div className="flex items-center gap-3">
                  {form.receipt_file_id ? (
                    <>
                      <a href={fileUrl(form.receipt_file_id)} target="_blank" rel="noreferrer"
                         data-testid="payment-receipt-link"
                         className="text-xs text-[var(--brand-primary)] underline">view current</a>
                      <button type="button" onClick={() => setForm({ ...form, receipt_file_id: "" })}
                              data-testid="payment-receipt-clear"
                              className="text-xs text-rose-700">remove</button>
                    </>
                  ) : (
                    <label className="text-sm cursor-pointer text-[#525252] hover:text-[var(--brand-primary)]">
                      <span data-testid="payment-receipt-upload">Choose file</span>
                      <input type="file" hidden accept="image/*,application/pdf" onChange={uploadReceipt}/>
                    </label>
                  )}
                </div>
              </Field>

              <div className="flex items-center gap-3 pt-4">
                <button type="submit" disabled={saving} data-testid="save-payment-button"
                        className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-60 text-white px-5 py-2 rounded-sm font-medium">
                  {saving ? "Saving…" : editing === "new" ? "Record payment" : "Save changes"}
                </button>
                <button type="button" onClick={() => setEditing(null)}
                        className="px-4 py-2 border border-black/10 rounded-sm text-sm">Cancel</button>
              </div>
            </div>
          </form>
        </div>
      )}

      <ConfirmDialog
        open={!!removing}
        title="Delete payment?"
        description={removing ? `Invoice ${removing.invoice_number} for ${removing.restaurant_name} will be removed permanently.` : ""}
        confirmLabel="Delete"
        tone="danger"
        onConfirm={remove}
        onCancel={() => setRemoving(null)}
      />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-2">{label}</label>
      {children}
    </div>
  );
}

function StatTile({ label, value, icon }) {
  return (
    <div className="border border-black/10 rounded-sm bg-white p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#525252] mb-2">{icon} {label}</div>
      <div className="font-display font-black text-2xl">{value}</div>
    </div>
  );
}

function CalloutList({ tone, icon, title, items, onDismiss }) {
  const cls = tone === "danger"
    ? "border-rose-200 bg-rose-50 text-rose-900"
    : "border-amber-200 bg-amber-50 text-amber-900";
  return (
    <div className={`border rounded-sm px-4 py-3 ${cls}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-medium mb-2">
        {icon} <span className="flex-1">{title}</span>
        {onDismiss && (
          <button type="button" onClick={onDismiss}
                  data-testid="callout-dismiss"
                  className="opacity-60 hover:opacity-100" aria-label="Dismiss">
            <X size={14}/>
          </button>
        )}
      </div>
      <ul className="space-y-1 text-sm">
        {items.map((it, i) => (
          <li key={`${it.label}-${i}`} className="flex justify-between gap-3">
            <span className="font-medium truncate">{it.label}</span>
            <span className="text-xs opacity-80 whitespace-nowrap">{it.hint}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
